import { useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { Button, Card, Input, Label } from '@iwoca/orion';
import clsx from 'clsx';
import { useRouterApplicationId } from 'components/Signup/hooks/useRouterApplicationId';
import { PeopleSchema } from 'components/Signup/SignupForm/SignUpForm.schema';
import { useAmplitude } from 'hooks/useAmplitude';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { EMPTY_ADDRESS } from 'utils/applications/signup.config';
import { PRIVACY_FIELD_CLASS } from 'utils/privacyClasses';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import * as yup from 'yup';
import { MultiAddressInput } from '../../MultiAddressInput/MultiAddressInput';
import { CriteriaItem } from '../../NewSignupForm/CriteriaItem/CriteriaItem';
import { DirectorSearch } from '../../NewSignupForm/DirectorSearch/DirectorSearch';
import signupFormStyles from '../../SignupForm/SignupForm.module.css';
import { TSignupFormData } from '../../SignupForm/SignupForm.types';
import CheckDetailsErrors from '../CheckDetailsErrors/CheckDetailsErrors';
import { useCheckDetails } from '../useCheckDetails';
import styles from './CheckDetailsForm.module.css';

type TProps = Pick<
  ReturnType<typeof useCheckDetails>,
  'normalisedState' | 'updateStateData'
> & {
  hasApplicantDetailsError?: boolean;
  hasCreditCheckError?: boolean;
  hasEquifaxTokenOnPrimaryAddress?: boolean;
};

export const CheckDetailsForm = ({
  normalisedState,
  hasApplicantDetailsError,
  hasCreditCheckError,
  hasEquifaxTokenOnPrimaryAddress,
  updateStateData,
}: TProps) => {
  const [selectedPerson, setSelectedPerson] =
    useState<GetApiIntroducerPortalDirectorSearchResponse['data'][number]>();
  const [isLoading, setIsLoading] = useState(false);

  const { trackEvent } = useAmplitude();
  const applicationId = useRouterApplicationId();
  const navigate = useNavigate();

  const normalisedStateWithoutErroredFields = () => {
    const defaultValues = {
      applicant: normalisedState.applicant,
    };

    if (!defaultValues.applicant) {
      return;
    }

    if (hasApplicantDetailsError) {
      defaultValues.applicant = {
        ...defaultValues.applicant,
        firstName: undefined,
        lastName: undefined,
        dobDay: undefined,
        dobMonth: undefined,
        dobYear: undefined,
      };
    }

    if (hasCreditCheckError) {
      const applicantAddresses = defaultValues.applicant?.residentialAddresses;

      const updatedPrimaryAddress = hasEquifaxTokenOnPrimaryAddress
        ? applicantAddresses[0]
        : EMPTY_ADDRESS;

      const updatedApplicantAddresses = [
        updatedPrimaryAddress,
        ...applicantAddresses.slice(1),
      ];

      defaultValues.applicant = {
        ...defaultValues.applicant,
        residentialAddresses: updatedApplicantAddresses,
      };
    }

    return defaultValues;
  };

  const methods = useForm<TSignupFormData>({
    resolver: yupResolver(
      yup.object().shape({
        applicant: PeopleSchema,
      }),
    ),
    defaultValues: { ...normalisedStateWithoutErroredFields() },
  });

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (values: TSignupFormData) => {
    trackEvent(AMPLITUDE_EVENTS.CTAClicked, {
      'CTA type': 'Submit',
      'CTA text': 'Update',
    });
    setIsLoading(true);
    await updateStateData(values);
    IwQueryClient.getOrCreate().invalidateQueries({
      queryKey: ['offers'],
    });
    navigate(PATH_NAMES.newApplicationProductOffers({ applicationId }));
    setIsLoading(false);
  };

  const setSelectedPersonWithDOB = (
    person:
      | GetApiIntroducerPortalDirectorSearchResponse['data'][number]
      | undefined,
  ) => {
    setSelectedPerson(person);
    setValue('applicant.firstName', person?.first_name || '');
    setValue('applicant.lastName', person?.last_name || '');
    setValue('applicant.dobMonth', person?.month_of_birth?.toString() || '');
    setValue('applicant.dobYear', person?.year_of_birth?.toString() || '');
  };

  const passesDirectorCheck = useMemo(() => {
    return selectedPerson?.roles?.includes('director') ?? false;
  }, [selectedPerson]);

  return (
    <FormProvider {...methods}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <Card className={clsx(signupFormStyles.formSection, styles.card)}>
          <h2 className={styles.heading}>Applicant details</h2>
          <CheckDetailsErrors
            hasApplicantDetailsError={hasApplicantDetailsError}
            hasCreditCheckError={hasCreditCheckError}
            hasMissingEquifaxToken={!hasEquifaxTokenOnPrimaryAddress}
          />
          {hasApplicantDetailsError && (
            <>
              <div className={signupFormStyles.formField}>
                <DirectorSearch
                  label="Applicant's name"
                  companyNumber={normalisedState.companyNumber}
                  onChange={setSelectedPersonWithDOB}
                />
                <div className={styles.criteria}>
                  <CriteriaItem
                    disabled={!selectedPerson}
                    hasPassed={passesDirectorCheck}
                    testId="director"
                  >
                    The main applicant must be a director of the company and
                    prepared to provide a personal guarantee to support a loan.
                  </CriteriaItem>
                </div>
              </div>
              <div className={signupFormStyles.formField}>
                <Label>Date of birth</Label>
                <div className={styles.dobContainer}>
                  <Input
                    {...register('applicant.dobDay')}
                    placeholder="DD"
                    error={errors.applicant?.dobDay?.message}
                    disabled={!selectedPerson}
                    className={PRIVACY_FIELD_CLASS}
                  />
                  <Input
                    {...register('applicant.dobMonth')}
                    placeholder="MM"
                    error={errors.applicant?.dobMonth?.message}
                    disabled={!selectedPerson}
                    className={PRIVACY_FIELD_CLASS}
                  />
                  <Input
                    {...register('applicant.dobYear')}
                    placeholder="YYYY"
                    error={errors.applicant?.dobYear?.message}
                    disabled={!selectedPerson}
                    className={PRIVACY_FIELD_CLASS}
                  />
                </div>
              </div>
            </>
          )}
          <div className={signupFormStyles.formField}>
            <MultiAddressInput personRole="applicant" personIndex={0} />
          </div>
        </Card>
        <div className={styles.buttonContainer}>
          <Button className={styles.button} type="submit" loading={isLoading}>
            Update
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
